import PushPin from '@mui/icons-material/PushPin';
import SpeakerNotes from '@mui/icons-material/SpeakerNotes';
import { SxProps, Theme } from '@mui/material/styles';
import React from 'react';

import { Note, TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';

import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { List, ListProps } from '_lib/components/List/List';
import { ListItem, ListItemProps } from '_lib/components/ListItem/ListItem';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { toSxArray } from '_lib/utils/helpers';

export interface NoteListProps extends ListProps {
  notes: Note[];
  timezone: TimezoneAbbreviationEnum;
  displayAsToday?: boolean;
  displayShowMore?: boolean;
  divider?: ListItemProps['divider'];
  listItemSx?: SxProps<Theme>;
}

export const NoteList = (props: NoteListProps): React.ReactElement => {
  const { notes, timezone, displayAsToday, displayShowMore, divider, sx, listItemSx, ...restListProps } = props;

  return (
    <List sx={[{ color: 'inherit' }, ...toSxArray(sx)]} {...restListProps}>
      {notes.map((note, index) => (
        <ListItem key={index} sx={listItemSx} divider={divider && index !== notes.length - 1}>
          <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
            <FlexBox>{note.isPinned ? <PushPin /> : <SpeakerNotes />}</FlexBox>
            <Stack spacing={0.5}>
              {displayShowMore ? (
                <ShowMoreText text={note.text} />
              ) : (
                <Typography text={note.text.trim()} sx={{ whiteSpace: 'pre-wrap' }} />
              )}
              <Stack direction='row' spacing={0.5}>
                <Typography sx={{ display: 'inline' }} variant='body1Bold' text='modified: ' />
                <Typography
                  sx={{ display: 'inline' }}
                  text={
                    displayAsToday
                      ? DateUtils.todayOrFormat(note.modifiedAt, {
                          timezone
                        })
                      : DateUtils.format(note.modifiedAt, DateFormatEnum.DateAtTime, {
                          timezone,
                          showTimezone: true
                        })
                  }
                />
              </Stack>
            </Stack>
          </Stack>
        </ListItem>
      ))}
    </List>
  );
};
